import * as _ from 'lodash';
import * as moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Label, OverlayTrigger, Popover } from 'react-bootstrap';
import ReactLoading from 'react-loading';
import { IWatrRequest } from '@nkr/models/index';
import { GridWrapper, HeaderWrapper } from '../styles';
import { DsArea, WageAndTravelType, WageAndTravelStatus, WageAndTravelCategory, WageAndTravelViewType } from '@nkr/enums/index';
import { isAdvancedDsArea } from './watr-nav-area';
import { calculateLwrTotal, getDerColumnDisplayAmount, getEligibleLwrWeekCount, getLwrWeeklyDisplayAmount, getRowTotalsForPendingHeader, hasPriorDerReimbursement, usesPendWireAmountLogicOnPendAch } from './utils/watr-grid-totals.utils';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, CellClickedEvent, ICellRendererParams, ValueGetterParams, GridReadyEvent, FirstDataRenderedEvent, IHeaderParams, ColDef, SortChangedEvent, GridSizeChangedEvent, BodyScrollEvent, GridColumnsChangedEvent } from 'ag-grid-community';
import { WatrAction } from './watr-action';
import { Utility } from '@nkr/services/utility';
import { DonorShieldContext } from './context';
import ActionsDropdown from './actions-dropdown';
import PendingRenderer, { resolvePendingCell, renderPendingActionButton, IPendingRendererProps } from './pending-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import FraudFlagPopover from './modals/fraud-flag-popover';
import {
    getWatrGridStatusDisplay,
    getWatrGridStatusShortDisplay,
    getWatrGridStatusTooltip,
    INTL_POST_WORKUP_STATUS_DISPLAY,
    isAdvancedInviteDeliveryIssue,
    isAdvancedStatusUnavailable,
    readAdvancedProgressForBuckets,
    resolveAdvancedRollupBucket,
    readIsInternationallyEligibleForWatr,
} from './advanced-reimbursement-status-buckets';

interface IProps {
    list: IWatrRequest[],
    gridApi: GridApi,
    columnApi: ColumnApi,
    area: DsArea,
    selectedTab: WageAndTravelCategory,
    isSearchView?: boolean,
    height: string,
    isCenterView?: boolean,
    isLoading?: boolean,
    onSetGridComponents: (params: GridReadyEvent) => void,
    onActionRequest: (id: number, viewType: WageAndTravelViewType) => void,
    resendInvitation: (id: number) => void,
    onAdvancedWorkflowAction: (id: number, action: 'approve' | 'deny' | 'hold' | 'release' | 'activate') => void,
    onReconcileTransactions: (id: number) => void
}

const WatrAgGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const context = useContext(DonorShieldContext);
    const isAdvancedReimbursement = isAdvancedDsArea(props.area);

    const getAdvancedProgress = readAdvancedProgressForBuckets;

    const parseAdvancedMilestoneDate = (rawValue: string | undefined | null) => {
        if (!rawValue) {
            return null;
        }
        const parsed = moment(rawValue, ['MM/DD/YYYY', 'MM/DD/YYYY HH:mm', moment.ISO_8601], true);
        return parsed.isValid() ? parsed : null;
    };

    const DonorRemoteStatusId = {
        NotContacted: 0,
        OnHold: 1,
        PendingWorkup: 5,
        WorkupScheduled: 10,
        WorkupComplete: 15,
        DonorApproved: 20,
        PreOpScheduled: 25,
        PreOpCompleted: 30,
        Donated: 100,
        RuledOut: 200,
        DroppedOut: 205,
    };

    type NetworkMilestoneKind = 'workup' | 'preop' | 'surgery' | 'closed' | 'hold' | null;

    const deriveNetworkMilestoneFromRemoteStatus = (
        remoteStatus: number | undefined,
        workupDate: string | undefined,
        preOpDate: string | undefined,
        surgeryDate: string | undefined,
        donationDate: string | undefined
    ): { label: string, dateRaw: string | null, kind: NetworkMilestoneKind } => {
        const workupParsed = parseAdvancedMilestoneDate(workupDate);
        const preOpParsed = parseAdvancedMilestoneDate(preOpDate);
        const surgeryParsed = parseAdvancedMilestoneDate(surgeryDate);
        const donatedDateRaw = surgeryDate || donationDate || null;

        if (remoteStatus === DonorRemoteStatusId.RuledOut) {
            return { label: 'Ruled out', dateRaw: null, kind: 'closed' };
        }

        if (remoteStatus === DonorRemoteStatusId.DroppedOut) {
            return { label: 'Dropped out', dateRaw: null, kind: 'closed' };
        }

        if (remoteStatus === DonorRemoteStatusId.OnHold) {
            return { label: 'On hold', dateRaw: null, kind: 'hold' };
        }

        if (remoteStatus === DonorRemoteStatusId.Donated) {
            return { label: 'Donation complete', dateRaw: donatedDateRaw, kind: 'surgery' };
        }

        if (remoteStatus != null
            && remoteStatus >= DonorRemoteStatusId.PreOpScheduled
            && remoteStatus < DonorRemoteStatusId.Donated) {
            if (remoteStatus >= DonorRemoteStatusId.PreOpCompleted) {
                return { label: 'Pre-op complete', dateRaw: preOpDate || null, kind: 'preop' };
            }
            return { label: 'Pre-op scheduled', dateRaw: preOpDate || null, kind: 'preop' };
        }

        if (remoteStatus != null
            && remoteStatus >= DonorRemoteStatusId.WorkupComplete
            && remoteStatus < DonorRemoteStatusId.PreOpScheduled) {
            return { label: 'Workup complete', dateRaw: workupDate || null, kind: 'workup' };
        }

        if (remoteStatus === DonorRemoteStatusId.WorkupScheduled
            || remoteStatus === DonorRemoteStatusId.PendingWorkup) {
            return { label: 'Workup scheduled', dateRaw: workupDate || null, kind: 'workup' };
        }

        if (surgeryParsed) {
            return { label: 'Surgery scheduled', dateRaw: donatedDateRaw, kind: 'surgery' };
        }
        if (preOpParsed) {
            return { label: 'Pre-op scheduled', dateRaw: preOpDate || null, kind: 'preop' };
        }
        if (workupParsed) {
            return { label: 'Workup scheduled', dateRaw: workupDate || null, kind: 'workup' };
        }

        return { label: '', dateRaw: null, kind: null };
    };

    const resolveAdvancedNetworkMilestone = (data: IWatrRequest): { label: string, dateRaw: string | null, kind: NetworkMilestoneKind } => {
        const progress = getAdvancedProgress(data);
        return deriveNetworkMilestoneFromRemoteStatus(
            progress?.networkRemoteStatus,
            progress?.workupScheduledDate,
            progress?.preOpScheduledDate,
            progress?.surgeryScheduledDate,
            data.donationDate && moment(data.donationDate).isValid()
                ? moment(data.donationDate).format('YYYY-MM-DD')
                : undefined
        );
    };

    const isMilestoneDatePassed = (kind: NetworkMilestoneKind, dateRaw: string | null) => {
        if (!dateRaw || kind === 'closed' || kind === 'hold') {
            return false;
        }
        if (kind === 'surgery') {
            return moment(dateRaw).isValid() && moment(dateRaw).isSameOrBefore(moment(), 'day');
        }
        const parsed = parseAdvancedMilestoneDate(dateRaw);
        return !!parsed && parsed.isSameOrBefore(moment(), 'day');
    };

    const isMilestoneDateFuture = (kind: NetworkMilestoneKind, dateRaw: string | null) => {
        if (!dateRaw || kind === 'closed' || kind === 'hold') {
            return false;
        }
        if (kind === 'surgery') {
            return moment(dateRaw).isValid() && moment(dateRaw).isAfter(moment(), 'day');
        }
        const parsed = parseAdvancedMilestoneDate(dateRaw);
        return !!parsed && parsed.isAfter(moment(), 'day');
    };

    const getNetworkStatusDisplay = (label: string, kind: NetworkMilestoneKind, dateRaw: string | null) => {
        const normalized = label.toLowerCase();

        if (kind === 'hold') {
            return {
                shortLabel: label,
                icon: 'glyphicon-pause' as string | null,
                bsStyle: 'warning' as 'success' | 'warning' | 'default' | 'danger',
                dateDisplay: '',
                explanation: 'Remote Status is On Hold. Milestone progression is paused.',
                phase: label,
                state: 'hold' as 'complete' | 'scheduled' | 'closed' | 'hold',
                datePassed: false,
            };
        }

        if (kind === 'closed') {
            return {
                shortLabel: label,
                icon: 'glyphicon-remove' as string | null,
                bsStyle: 'danger' as 'success' | 'warning' | 'default' | 'danger',
                dateDisplay: '',
                explanation: normalized.includes('dropped')
                    ? 'Remote Status is Dropped Out. This donor is closed and did not donate.'
                    : 'Remote Status is Ruled Out. This donor is closed and did not donate.',
                phase: label,
                state: 'closed' as 'complete' | 'scheduled' | 'closed' | 'hold',
                datePassed: false,
            };
        }

        const phase = kind === 'preop'
            ? 'Pre-op'
            : kind === 'surgery'
                ? (normalized.includes('donation') ? 'Donation' : 'Surgery')
                : kind === 'workup'
                    ? 'Workup'
                    : 'Network';

        const isComplete = normalized.includes('complete');
        const datePassed = !isComplete && isMilestoneDatePassed(kind, dateRaw);
        const dateFuture = !isComplete && isMilestoneDateFuture(kind, dateRaw);
        const state: 'complete' | 'scheduled' = isComplete ? 'complete' : 'scheduled';

        const shortLabel = state === 'complete' ? `${phase} complete` : `${phase} scheduled`;

        const icon = state === 'complete'
            ? 'glyphicon-ok'
            : datePassed
                ? 'glyphicon-time'
                : null;

        const bsStyle: 'success' | 'warning' | 'default' | 'danger' = state === 'complete' || dateFuture
            ? 'success'
            : datePassed
                ? 'warning'
                : 'default';

        let dateDisplay = '';
        if (kind === 'surgery') {
            dateDisplay = moment(dateRaw).isValid() ? moment(dateRaw).format('MM/DD/YYYY') : '';
            if (!dateDisplay) {
                const parsed = parseAdvancedMilestoneDate(dateRaw);
                dateDisplay = parsed ? parsed.format('MM/DD/YYYY') : '';
            }
        } else {
            const parsed = parseAdvancedMilestoneDate(dateRaw);
            dateDisplay = parsed ? parsed.format('MM/DD/YYYY') : '';
        }

        const explanations: Record<string, string> = {
            'workup|scheduled|future': 'Workup is scheduled. Status has not been confirmed yet — it is not complete until Remote Status is set to Workup Complete.',
            'workup|scheduled|passed': 'Workup was scheduled, but the status has not been confirmed yet. The workup date has passed and Remote Status is still not Workup Complete.',
            'workup|complete': 'Remote Status is Workup Complete. Workup has been confirmed.',
            'preop|scheduled|future': 'Pre-op is scheduled. Status has not been confirmed yet — it is not complete until Remote Status is Pre-Op Completed.',
            'preop|scheduled|passed': 'Pre-op was scheduled, but the status has not been confirmed yet. The pre-op date has passed and Remote Status is still not Pre-Op Completed.',
            'preop|complete': 'Remote Status is Pre-Op Completed. Pre-op has been confirmed.',
            'surgery|scheduled|future': 'Surgery/donation is scheduled. Status has not been confirmed yet.',
            'surgery|scheduled|passed': 'Surgery/donation was scheduled, but the status has not been confirmed yet. The date has passed and Remote Status is still not Donated.',
            'surgery|complete': 'Remote Status is Donated. Donation has been confirmed.',
        };

        const explanationKey = state === 'complete'
            ? `${kind || 'workup'}|complete`
            : `${kind || 'workup'}|scheduled|${datePassed ? 'passed' : 'future'}`;
        const explanation = explanations[explanationKey] || label;

        return { shortLabel, icon, bsStyle, dateDisplay, explanation, phase, state, datePassed };
    };

    const NETWORK_STATUS_STYLE_RANK: Record<'warning' | 'default' | 'success' | 'danger', number> = {
        warning: 0,
        danger: 1,
        default: 2,
        success: 3,
    };

    const getNetworkStatusStyleRank = (data: IWatrRequest) => {
        const resolved = resolveAdvancedNetworkMilestone(data);
        const display = getNetworkStatusDisplay(resolved.label, resolved.kind, resolved.dateRaw);
        return NETWORK_STATUS_STYLE_RANK[display.bsStyle];
    };

    const renderNetworkStatusLabel = (data: IWatrRequest) => {
        const { label, dateRaw, kind } = resolveAdvancedNetworkMilestone(data);
        if (!label) {
            return <>--</>;
        }

        const display = getNetworkStatusDisplay(label, kind, dateRaw);
        const progress = getAdvancedProgress(data);
        const remoteStatus = progress?.networkRemoteStatus;
        const remoteStatusName = remoteStatus === DonorRemoteStatusId.WorkupScheduled ? 'Workup Scheduled'
            : remoteStatus === DonorRemoteStatusId.WorkupComplete ? 'Workup Complete'
            : remoteStatus === DonorRemoteStatusId.DonorApproved ? 'Donor Approved'
            : remoteStatus === DonorRemoteStatusId.PreOpScheduled ? 'Pre-Op Scheduled'
            : remoteStatus === DonorRemoteStatusId.PreOpCompleted ? 'Pre-Op Completed'
            : remoteStatus === DonorRemoteStatusId.Donated ? 'Donated'
            : remoteStatus === DonorRemoteStatusId.RuledOut ? 'Ruled Out'
            : remoteStatus === DonorRemoteStatusId.DroppedOut ? 'Dropped Out'
            : remoteStatus === DonorRemoteStatusId.OnHold ? 'On Hold'
            : remoteStatus === DonorRemoteStatusId.PendingWorkup ? 'Pending Workup'
            : remoteStatus != null ? `Status ${remoteStatus}` : null;
        const popover = (
            <Popover id={`network-status-${data.id}`} title={`${display.phase} status`}>
                <div style={{ marginBottom: 6 }}>{display.explanation}</div>
                {display.dateDisplay && (
                    <div><strong>Date:</strong> {display.dateDisplay}</div>
                )}
                {remoteStatusName && (
                    <div style={{ marginTop: 6 }}><strong>Remote Status:</strong> {remoteStatusName}</div>
                )}
            </Popover>
        );

        return (
            <OverlayTrigger
                trigger={['hover', 'focus', 'click']}
                placement='top'
                delayShow={200}
                delayHide={150}
                rootClose
                container={typeof document !== 'undefined' ? () => document.body : undefined}
                overlay={popover}
            >
                <span
                    role='button'
                    tabIndex={0}
                    style={{ cursor: 'help', display: 'inline-block', maxWidth: '100%', minWidth: 0 }}
                >
                    <Label bsStyle={display.bsStyle} style={{ fontSize: '100%', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                        {display.shortLabel}
                        {display.icon && (
                            <span className={`glyphicon ${display.icon}`} style={{ marginLeft: 5 }} aria-hidden='true' />
                        )}
                    </Label>
                </span>
            </OverlayTrigger>
        );
    };

    const advancedReimbursementStatusStyle = (data: IWatrRequest): 'success' | 'info' | 'warning' | 'danger' | 'default' | 'primary' => {
        if (getWatrGridStatusDisplay(data, isAdvancedReimbursement) === INTL_POST_WORKUP_STATUS_DISPLAY) {
            return 'info';
        }
        if (isAdvancedInviteDeliveryIssue(data)) {
            return 'danger';
        }
        if (isAdvancedStatusUnavailable(data)) {
            return 'warning';
        }
        let b = resolveAdvancedRollupBucket(data);
        if (b == 'COMPLETE') {
            return 'primary';
        }
        if (b == 'TODO_NKR') {
            return 'info';
        }
        if (b == 'PENDING_DONOR' || b == 'PENDING_BANK_SUBMISSIONS') {
            return 'warning';
        }
        if (b == 'ON_HOLD') {
            return 'default';
        }
        if (b == 'DECLINED') {
            return 'danger';
        }
        return 'default';
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleItemSelected = (requestId: number, actionType: WatrAction) => {
        switch (actionType) {
            case WatrAction.Edit: props.onActionRequest(requestId, WageAndTravelViewType.RequestData); break;
            case WatrAction.ConfirmDonation: props.onActionRequest(requestId, WageAndTravelViewType.ConfirmDonation); break;
            case WatrAction.RequestAddlDays: props.onActionRequest(requestId, WageAndTravelViewType.AdditionalDays); break;
            case WatrAction.Audit: props.onActionRequest(requestId, WageAndTravelViewType.ReimburseWageAudit); break;
            case WatrAction.TravelDetails: props.onActionRequest(requestId, WageAndTravelViewType.ReimburseTravel); break;
            case WatrAction.CancelRequest: props.onActionRequest(requestId, WageAndTravelViewType.CancelRequest); break;
            case WatrAction.ResendInvitation: props.resendInvitation(requestId); break;
            case WatrAction.ApproveWeekReimbursements: props.onActionRequest(requestId, WageAndTravelViewType.ApproveWeekReimbursements); break;
            case WatrAction.ReimburseTravel: props.onActionRequest(requestId, WageAndTravelViewType.ReimburseTravel); break;
            case WatrAction.ReimburseWages: props.onActionRequest(requestId, WageAndTravelViewType.ReimburseWage); break;
            case WatrAction.VerifyTravel: props.onActionRequest(requestId, WageAndTravelViewType.VerifyTravel); break;
            case WatrAction.VerifyWages: props.onActionRequest(requestId, WageAndTravelViewType.VerifyStub); break;
            case WatrAction.SetPaymentError: props.onActionRequest(requestId, WageAndTravelViewType.PaymentError); break;
            case WatrAction.ResolvePaymentError: props.onActionRequest(requestId, WageAndTravelViewType.PaymentError); break;
            case WatrAction.RequestDonorReview:
                if (context?.onShowDeclineModal) {
                    context.onShowDeclineModal(requestId, false);
                }
                break;
            case WatrAction.ApproveAdvancedReimbursement: props.onAdvancedWorkflowAction(requestId, 'approve'); break;
            case WatrAction.DenyAdvancedReimbursement: props.onAdvancedWorkflowAction(requestId, 'deny'); break;
            case WatrAction.HoldAdvancedReimbursement: props.onAdvancedWorkflowAction(requestId, 'hold'); break;
            case WatrAction.ReleaseAdvancedReimbursement: props.onAdvancedWorkflowAction(requestId, 'release'); break;
            case WatrAction.ActivateAdvancedReimbursement: props.onAdvancedWorkflowAction(requestId, 'activate'); break;
            case WatrAction.ReconcileTransactions: props.onReconcileTransactions(requestId); break;
        }
    };

    const getDonorTypeAbbreviation = (description: string) => {
        let index = context.donationTypes.findIndex(donationType => donationType.description === description);

        if (index > -1) {
            return context.donationTypes[index].abbreviation;
        } else {
            return description;
        }
    }

    const getHeaderTotals = (data: IWatrRequest[]): { lwrTotal: number, derTotal: number, transactionTotal: number } => {
        return data.reduce((acc, r) => {
            const row = getRowTotalsForPendingHeader(r, props.selectedTab);
            acc.lwrTotal += row.lwrTotal;
            acc.derTotal += row.derTotal;
            acc.transactionTotal += row.transactionTotal;
            return acc;
        }, { lwrTotal: 0, derTotal: 0, transactionTotal: 0 });
    }

    const getColumnChanges = (selectedTab: WageAndTravelCategory) => {
        let columns: ColDef[] = [
            {
                colId: '1',
                field: 'rank',
                headerName: 'Rank',
                headerComponentFramework: (props: IHeaderParams) =>
                    <div>R#</div>,
                sortable: false,
                width: 60,
                minWidth: 55,
                maxWidth: 70,
                suppressSizeToFit: true,
                pinned: 'left',
                pinnedRowCellRendererFramework: () => <div></div>,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
            },

            {
                field: 'donationOrganType',
                headerName: 'donationOrganType',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Organ<br />Type</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 88,
                minWidth: 80,
                sort: 'asc',
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let organType = data.donationOrganType == null ? '--' : data.donationOrganType == 1 ? 'L' : 'K';
                    return <Label bsStyle={data.donationOrganType == null ? 'default' : data.donationOrganType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
                }
            },

            {
                field: 'donationType',
                headerName: 'Donation Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donation<br /> Type</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                minWidth: 90,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;

                    if (data.dsdRecord != null) {
                        return <Label bsStyle='warning' style={{ fontSize: '100%' }}>DSD</Label>
                    }

                    if (data.donorId > 0) {
                        return <Label bsStyle='info' style={{ fontSize: '100%' }}>{getDonorTypeAbbreviation(data.donationTypeDesc)}</Label>
                    }

                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>Unknown</Label>
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IWatrRequest;

                    if (data.dsdRecord != null) {
                        return 'DSD';
                    }

                    if (data.donorId > 0) {
                        return data.donationTypeDesc;
                    }

                    return 'Unknown';
                }
            },

            {
                field: 'watrRequestType',
                headerName: 'Request Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Request<br /> Type</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let style = 'danger';
                    let label = 'Unknown';

                    if (data.watrRequestType == WageAndTravelType.ReimburseWagesOnly) {
                        style = 'success';
                        label = 'Wages';
                    } else if (data.watrRequestType == WageAndTravelType.ReimburseTravelOnly) {
                        style = 'info';
                        label = 'Expenses';
                    } else if (data.watrRequestType == WageAndTravelType.ReimburseBoth) {
                        style = 'default';
                        label = 'Both';
                    } else {
                    }

                    return <Label bsStyle={style} style={{ fontSize: '100%' }}>{label}</Label>
                }
            },

            {
                field: 'achReimbursementType',
                headerName: 'Request Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Request Type</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 136,
                minWidth: 130,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let style = 'danger';
                    let label = 'Unknown';

                    if (data.achReimbursementType == WageAndTravelType.ReimburseWagesOnly) {
                        style = 'success';
                        label = 'Wages';
                    } else if (data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly) {
                        style = 'info';
                        label = 'Expenses';
                    } else if (data.achReimbursementType == WageAndTravelType.ReimburseBoth) {
                        style = 'default';
                        label = 'Both';
                    } else {
                    }

                    return <Label bsStyle={style} style={{ fontSize: '100%' }}>{label}</Label>
                }
            },

            {
                field: 'centerAlias',
                headerName: 'Center',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Center</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 112,
                minWidth: 108,
                cellStyle: { textAlign: 'left' },
                cellClass: 'watr-center-cell-left',
                headerClass: 'watr-center-header-left',
            },
            {
                field: 'donorAlias',
                headerName: 'Donor Alias',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor Alias</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 120,
                minWidth: 100,
                cellClass: 'watr-donor-alias-cell-left',
                headerClass: 'watr-donor-alias-header-left',
                cellStyle: { textAlign: 'left' },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let alias = data.donorAlias;

                    if (data.donorId > 0) {
                        return (
                            <a href={'/center/' + data.centerId + '/donor/' + data.donorId + '/profile/donor-data'} target='_blank'>
                                {alias}
                            </a>
                        );
                    } else if (data.dsdRecord != null) {
                        return (
                            <a href={`center/${data.centerId}/donors/donor-shield-direct/edit-donor/${data.dsdRecord.id}`}>
                                {alias}
                            </a>
                        );
                    }

                    return <>{alias}</>;
                }
            },

            {
                field: 'name',
                headerName: 'Name',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Name</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 200,
                minWidth: 120,
                flex: 1,
                cellStyle: { textAlign: 'left' },
                cellClass: 'watr-name-cell-centered',
                headerClass: 'watr-name-header-center',
                valueGetter: (params: ValueGetterParams) => params.data.name.toLowerCase(),
                cellRendererFramework: (params: ICellRendererParams) => {
                    return (
                        <div className='watr-name-cell-center-wrap'>
                            <div className='watr-name-cell-text' title={params.data.name}>
                                {params.data.name}
                            </div>
                        </div>
                    );
                }
            },

            {
                field: 'birthDate',
                headerName: 'Birth Date',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Birth<br />Date</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                minWidth: 100,
                valueGetter: (params: ValueGetterParams) => {
                    const raw = (params.data as IWatrRequest).birthDate;
                    return moment(raw).isValid() ? moment(raw).format('MM/DD/YYYY') : '';
                },
                comparator: Utility.dateComparator,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const raw = (params.data as IWatrRequest).birthDate;
                    return <>{moment(raw).isValid() ? moment(raw).format('MM/DD/YYYY') : ''}</>;
                }
            },

            {
                field: 'countryName',
                headerName: 'Country',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Country</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                minWidth: 90,
                valueGetter: (params: ValueGetterParams) => (params.data as IWatrRequest).countryName || '',
            },

            {
                field: 'createdDate',
                headerName: 'Created',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Created</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                minWidth: 100,
                valueGetter: (params: ValueGetterParams) => {
                    const raw = (params.data as IWatrRequest).createdDate;
                    return moment(raw).isValid() ? moment(raw).format('MM/DD/YYYY') : '';
                },
                comparator: Utility.dateComparator,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const raw = (params.data as IWatrRequest).createdDate;
                    return <>{moment(raw).isValid() ? moment(raw).format('MM/DD/YYYY') : ''}</>;
                }
            },

            {
                field: 'donationDate',
                headerName: 'Donation Date',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donation<br /> Date</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 130,
                minWidth: 120,
                valueGetter: (params: ValueGetterParams) =>
                    moment(params.data.donationDate).isValid()
                        ? moment(params.data.donationDate).format('MM/DD/YYYY')
                        : null,
                comparator: Utility.dateComparator,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let value = moment(data.donationDate).isValid() ? moment(data.donationDate).format('MM/DD/YYYY') : 'Pending';

                    return <>{value}</>
                }
            },

            {
                field: 'isInternationallyEligible',
                headerName: 'isInternationallyEligible',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Intl<br />Donor</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 78,
                minWidth: 72,
                valueGetter: (params: ValueGetterParams) =>
                    readIsInternationallyEligibleForWatr(params.data as IWatrRequest) ? 'Yes' : 'No',
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let isIntlDonor = readIsInternationallyEligibleForWatr(data);
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            {isIntlDonor && (
                                <span
                                    className="glyphicon glyphicon-ok"
                                    style={{ color: '#3c763d' }}
                                    aria-label="International donor"
                                />
                            )}
                        </div>
                    );
                }
            },

            {
                field: 'donationConfirmed',
                headerName: 'Donation Confirmed?',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Ready for<br /> Reimb</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 84,
                minWidth: 76,
                valueGetter: (params: ValueGetterParams) => params.data.donationConfirmed ? 'Yes' : 'No',
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            {data.donationConfirmed && (
                                <span
                                    className="glyphicon glyphicon-ok"
                                    style={{ color: '#3c763d' }}
                                    aria-label="Ready for reimbursement"
                                />
                            )}
                        </div>
                    );
                }
            },

            {
                field: 'achRoutingNumber',
                headerName: 'achRoutingNumber',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Routing<br />Number</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 75,
            },

            {
                field: 'bankInfo',
                headerName: 'bankInfo',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Bank Info</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 170,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const d: IWatrRequest = params.data;

                    if (d.bankInfo == 'Bank not found') {
                        return (
                            <Label bsStyle='danger'
                                style={{ fontSize: '100%' }}
                                onClick={() => {
                                    props.onActionRequest(d.id, WageAndTravelViewType.PaymentError);
                                }}
                            >
                                {d.bankInfo}
                            </Label>
                        );
                    } else {
                        return (
                            <div style={{ width: '100%', textAlign: 'left' }} title={d.bankInfo}>
                                {Utility.shortenString(d.bankInfo, 15)}
                            </div>
                        );
                    }
                }
            },

            {
                field: 'fraudFlags',
                headerName: 'fraudFlags',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Possible<br />Bank Error</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 170,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const d: IWatrRequest = params.data;
                    return d.fraudFlags.length > 0 ? <FraudFlagPopover onLookup={(f) => props.onActionRequest(f.relatedRequestId, WageAndTravelViewType.RequestData)} flags={d.fraudFlags} /> : <></>;
                }
            },

            {
                field: 'status',
                headerName: 'Status',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Status</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 180,
                minWidth: 150,
                cellClass: 'watr-status-pill-cell',
                valueGetter: (params: ValueGetterParams) => {
                    const data = params.data as IWatrRequest;
                    return getWatrGridStatusDisplay(data, isAdvancedReimbursement);
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    const displayStatus = getWatrGridStatusDisplay(data, isAdvancedReimbursement);
                    const shortStatus = getWatrGridStatusShortDisplay(displayStatus);
                    const isPostWorkup = displayStatus === INTL_POST_WORKUP_STATUS_DISPLAY;
                    const labelStyle = isAdvancedReimbursement
                        ? advancedReimbursementStatusStyle(data)
                        : (data.status == WageAndTravelStatus.Donated ? 'success' : isPostWorkup ? 'info' : 'warning');
                    const title = getWatrGridStatusTooltip(data, displayStatus) || displayStatus;

                    return (
                        <Label
                            bsStyle={labelStyle}
                            style={{
                                fontSize: '100%',
                                whiteSpace: 'nowrap',
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                verticalAlign: 'middle'
                            }}
                            title={title}
                        >
                            {shortStatus}
                        </Label>
                    );
                }
            },
            {
                field: 'advancedNetworkMilestoneLabel',
                headerName: 'Milestone Status',
                width: 150,
                minWidth: 130,
                hide: !isAdvancedReimbursement || !context.isNkrSuper,
                cellClass: 'watr-status-pill-cell',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Milestone<br />Status</HeaderWrapper>
                    </SortableHeaderComponent>,
                valueGetter: (params: ValueGetterParams) => {
                    const resolved = resolveAdvancedNetworkMilestone(params.data as IWatrRequest);
                    if (!resolved.label) {
                        return '';
                    }
                    return getNetworkStatusDisplay(resolved.label, resolved.kind, resolved.dateRaw).shortLabel;
                },
                comparator: (labelA: string, labelB: string, nodeA: any, nodeB: any) => {
                    const byLabel = (labelA || '').localeCompare(labelB || '');
                    if (byLabel !== 0) {
                        return byLabel;
                    }
                    return getNetworkStatusStyleRank(nodeA.data as IWatrRequest) - getNetworkStatusStyleRank(nodeB.data as IWatrRequest);
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    return renderNetworkStatusLabel(params.data as IWatrRequest);
                }
            },
            {
                field: 'advancedNetworkMilestoneDate',
                headerName: 'Milestone Date',
                width: 130,
                hide: !isAdvancedReimbursement || !context.isNkrSuper,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Milestone<br />Date</HeaderWrapper>
                    </SortableHeaderComponent>,
                valueGetter: (params: ValueGetterParams) => {
                    const { dateRaw, kind } = resolveAdvancedNetworkMilestone(params.data as IWatrRequest);
                    if (kind === 'closed' || kind === 'hold' || !dateRaw) {
                        return null;
                    }
                    if (kind === 'surgery') {
                        if (moment(dateRaw).isValid()) {
                            return moment(dateRaw).format('MM/DD/YYYY');
                        }
                        const parsedSurgery = parseAdvancedMilestoneDate(dateRaw);
                        return parsedSurgery ? parsedSurgery.format('MM/DD/YYYY') : null;
                    }
                    const parsed = parseAdvancedMilestoneDate(dateRaw);
                    return parsed ? parsed.format('MM/DD/YYYY') : null;
                },
                comparator: Utility.dateComparator,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const resolved = resolveAdvancedNetworkMilestone(params.data as IWatrRequest);
                    const { dateRaw, kind } = resolved;
                    if (kind === 'closed' || kind === 'hold' || !dateRaw) {
                        return kind === 'surgery' ? <>Pending</> : <>--</>;
                    }

                    const display = getNetworkStatusDisplay(resolved.label, kind, dateRaw);
                    let displayValue = '';
                    if (kind === 'surgery') {
                        displayValue = moment(dateRaw).isValid() ? moment(dateRaw).format('MM/DD/YYYY') : '';
                        if (!displayValue) {
                            const parsedSurgery = parseAdvancedMilestoneDate(dateRaw);
                            displayValue = parsedSurgery ? parsedSurgery.format('MM/DD/YYYY') : 'Pending';
                        }
                    } else {
                        const parsed = parseAdvancedMilestoneDate(dateRaw);
                        if (!parsed) {
                            return <>--</>;
                        }
                        displayValue = parsed.format('MM/DD/YYYY');
                    }

                    if (display.state === 'complete') {
                        return <Label bsStyle='success' style={{ fontSize: '100%' }}>{displayValue}</Label>;
                    }
                    if (display.datePassed) {
                        return <Label bsStyle='warning' style={{ fontSize: '100%' }}>{displayValue}</Label>;
                    }

                    return <Label bsStyle='info' style={{ fontSize: '100%' }}>{displayValue}</Label>;
                }
            },
            {
                field: 'lwrWeeklyAmount',
                headerName: 'LWR / Wk',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>LWR / Wk</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                minWidth: 90,
                hide: [
                    WageAndTravelCategory.PendingLWRVerify,
                    WageAndTravelCategory.PendingPayment,
                    WageAndTravelCategory.PendingWire,
                    WageAndTravelCategory.Search,
                    WageAndTravelCategory.PaymentError,
                    WageAndTravelCategory.PendSurgery
                ].indexOf(props.selectedTab) < 0 || props.list.some(f => f.pendingTransactionConfirmation),
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;

                    if (props.selectedTab == WageAndTravelCategory.PendingPayment
                        && data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly
                        && !usesPendWireAmountLogicOnPendAch(data)) {
                        return '--'
                    }

                    if (!data || !data.requestWeeks) return null;

                    const weekLwrAmount = getLwrWeeklyDisplayAmount(data);

                    return (
                        <div style={{ width: '100%', textAlign: 'right' }}>
                            {weekLwrAmount === 0 ? (
                                '--'
                            ) : (
                                <>
                                    ${Math.round(weekLwrAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </>
                            )}
                        </div>
                    );
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IWatrRequest;
                    return getLwrWeeklyDisplayAmount(data);
                },
            },
            {
                field: 'lwrWeekCount',
                headerName: 'Wks',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Wks</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 64,
                minWidth: 58,
                hide: [
                    WageAndTravelCategory.PendingLWRVerify,
                    WageAndTravelCategory.PendingPayment,
                    WageAndTravelCategory.PendingWire,
                    WageAndTravelCategory.Search,
                    WageAndTravelCategory.PaymentError,
                    WageAndTravelCategory.PendSurgery
                ].indexOf(props.selectedTab) < 0 || props.list.some(f => f.pendingTransactionConfirmation),
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;

                    if (props.selectedTab == WageAndTravelCategory.PendingPayment
                        && data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly
                        && !usesPendWireAmountLogicOnPendAch(data)) {
                        return '--'
                    }

                    if (!data || !data.requestWeeks) return null;

                    const weekCount = getEligibleLwrWeekCount(data);
                    const weekLwrAmount = getLwrWeeklyDisplayAmount(data);

                    return (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            {weekLwrAmount === 0 ? (
                                '--'
                            ) : (
                                <Label bsStyle='info' style={{ fontSize: '100%' }}>{weekCount}</Label>
                            )}
                        </div>
                    );
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IWatrRequest;
                    return getEligibleLwrWeekCount(data);
                },
            },
            {
                field: 'lwr',
                headerName: 'LWR',
                headerComponentFramework: (params: IHeaderParams) => (
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>
                            LWR
                            {([WageAndTravelCategory.PendingPayment, WageAndTravelCategory.PendingWire, WageAndTravelCategory.PaymentError, WageAndTravelCategory.PendingLWRVerify].indexOf(props.selectedTab) >= 0 || (context.isNkrSuper && props.selectedTab == WageAndTravelCategory.PendSurgery)) &&
                                <>
                                    <br />
                                    {`Total: $${getHeaderTotals(props.list).lwrTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                                </>
                            }
                        </HeaderWrapper>
                    </SortableHeaderComponent>
                ),
                width: 110,
                minWidth: 100,
                hide: [
                    WageAndTravelCategory.PendingLWRVerify,
                    WageAndTravelCategory.PendingPayment,
                    WageAndTravelCategory.Search,
                    WageAndTravelCategory.PendingWire,
                    WageAndTravelCategory.PaymentError,
                    WageAndTravelCategory.PendSurgery
                ].indexOf(props.selectedTab) < 0,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    if (props.selectedTab == WageAndTravelCategory.PendingPayment
                        && !usesPendWireAmountLogicOnPendAch(data)) {
                        if (data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly) {
                            return <div style={{ width: '100%', textAlign: 'right', fontWeight: 700 }}>--</div>;
                        }
                        let weekLwrTotal = calculateLwrTotal(data);
                        return (
                            <div style={{ width: '100%', textAlign: 'right', fontWeight: 700 }}>
                                {weekLwrTotal === 0 ? '--' : `$${Math.round(weekLwrTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                            </div>
                        );
                    }

                    let hideValue = false;

                    if (props.selectedTab == WageAndTravelCategory.PendSurgery && !moment(params.data.donationDate).isValid()) {
                        hideValue = true;
                    }

                    let weekLwrTotal = calculateLwrTotal(data);

                    return (
                        <div style={{ width: '100%', textAlign: 'right', fontWeight: 700 }}>
                            {(weekLwrTotal === 0 || hideValue) ? (
                                '--'
                            ) : (
                                <>
                                    ${Math.round(weekLwrTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </>
                            )}
                        </div>
                    );
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IWatrRequest;
                    if (props.selectedTab == WageAndTravelCategory.PendingPayment
                        && !usesPendWireAmountLogicOnPendAch(data)
                        && data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly) {
                        return 0;
                    }
                    return calculateLwrTotal(data);
                },
            },

            {
                field: 'tlr',
                headerName: 'DER',
                headerComponentFramework: (params: IHeaderParams) => (
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>
                            DER
                            {([WageAndTravelCategory.PendingPayment, WageAndTravelCategory.PendingWire, WageAndTravelCategory.PaymentError, WageAndTravelCategory.PendingDERVerify].indexOf(props.selectedTab) >= 0 || (context.isNkrSuper && props.selectedTab == WageAndTravelCategory.PendSurgery)) &&
                                <>
                                    <br />
                                    {`Total: $${getHeaderTotals(props.list).derTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                                </>
                            }
                        </HeaderWrapper>
                    </SortableHeaderComponent>
                ),
                width: 110,
                minWidth: 100,
                hide: [
                    WageAndTravelCategory.PendingDERVerify,
                    WageAndTravelCategory.PendingPayment,
                    WageAndTravelCategory.PendingWire,
                    WageAndTravelCategory.Search,
                    WageAndTravelCategory.PaymentError,
                    WageAndTravelCategory.PendSurgery
                ].indexOf(props.selectedTab) < 0,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    if (props.selectedTab == WageAndTravelCategory.PendingPayment
                        && !usesPendWireAmountLogicOnPendAch(data)) {
                        return data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly
                            ? ((params.value == 0 || params.value == null)
                                ? '--'
                                : `$${Math.round(params.value as number).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`)
                            : '--';
                    }

                    let hideValue = false;

                    if (props.selectedTab == WageAndTravelCategory.PendSurgery && !moment(params.data.donationDate).isValid()) {
                        hideValue = true;
                    }

                    const overCap = props.selectedTab === WageAndTravelCategory.Search
                        && (data.derProgramCap || 0) > 0
                        && (params.value as number) > (data.derProgramCap || 0);

                    return <div style={{ width: '100%', textAlign: 'right', color: overCap ? '#a94442' : undefined }}>
                        {(params.value == 0 || hideValue) ? '--' : '$' + Math.round(params.value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </div>
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IWatrRequest;
                    return getDerColumnDisplayAmount(data, props.selectedTab);
                },
            },

            {
                field: 'hasPriorDerReimbursement',
                headerName: 'Prior DER',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <OverlayTrigger
                            trigger={['hover', 'focus', 'click']}
                            placement='top'
                            delayShow={200}
                            delayHide={150}
                            rootClose
                            container={typeof document !== 'undefined' ? () => document.body : undefined}
                            overlay={
                                <Popover id='watr-grid-prior-der-header' title='Prior DER'>
                                    Donor already has a prior travel (DER) reimbursement; this claim is a second (or later) claim. Most donors have one; a second claim is uncommon.
                                </Popover>
                            }
                        >
                            <HeaderWrapper style={{ cursor: 'help' }}>Prior<br />DER</HeaderWrapper>
                        </OverlayTrigger>
                    </SortableHeaderComponent>,
                width: 78,
                minWidth: 72,
                maxWidth: 90,
                hide: props.selectedTab !== WageAndTravelCategory.PendingDERVerify,
                valueGetter: (params: ValueGetterParams) =>
                    hasPriorDerReimbursement(params.data as IWatrRequest) ? 1 : 0,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const isRepeat = hasPriorDerReimbursement(params.data as IWatrRequest);
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            {isRepeat && (
                                <span
                                    className="glyphicon glyphicon-ok"
                                    style={{ color: '#3c763d' }}
                                    aria-label="Second or later DER reimbursement"
                                />
                            )}
                        </div>
                    );
                },
            },

            {
                field: 'pendingTransactionConfirmation',
                headerName: 'pendingTransactionConfirmation',
                hide: !props.list.some(f => f.pendingTransactionConfirmation),
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Chase<br />Confirmation #</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    return <div>
                        {!data.pendingTransactionConfirmation ? '--' : data.pendingTransactionConfirmation}
                    </div>
                },
            },

            {
                field: 'pendingTransactionAmount',
                headerName: 'pendingTransactionAmount',
                hide: !props.list.some(f => f.pendingTransactionConfirmation),
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Confirmation <br /> Amount</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    return <div>
                        {!data.pendingTransactionAmount || data.pendingTransactionAmount == 0 ? '--' : `$${Math.round(data.pendingTransactionAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                    </div>
                },
            },

            {
                field: 'pendingTransactionSendOn',
                headerName: 'pendingTransactionSendOn',
                hide: !props.list.some(f => f.pendingTransactionConfirmation),
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Send On</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    return <div>
                        {!data.pendingTransactionSendOn ? '--' : data.pendingTransactionSendOn}
                    </div>
                },
            },

            {
                field: 'bankingFixesPending',
                headerName: 'bankingFixesPending',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Pending<br />Donor Review</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IWatrRequest;
                    const hasPendingFixes = !!(data.bankingRoutingRequiresReview || data.bankingAccountRequiresReview);
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            {hasPendingFixes && (
                                <span
                                    className="glyphicon glyphicon-time"
                                    aria-label="Pending donor review"
                                />
                            )}
                        </div>
                    );
                }
            },

            {
                field: 'confirmationButton',
                headerName: 'confirmationButton',
                hide: !props.list.some(f => f.pendingTransactionConfirmation),
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Reimbursement<br />Confirmation</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    let viewType = data.achReimbursementType == WageAndTravelType.ReimburseWagesOnly ? WageAndTravelViewType.ConfirmWageReiembursement : WageAndTravelViewType.ConfirmTravelReiembursement

                    if (data.pendingTransactionConfirmation) {
                        return (
                            <button
                                type="button"
                                className='btn btn-success btn-xs'
                                onClick={() => props.onActionRequest(data.id, viewType)}
                            >
                                Confirm
                            </button>
                        );
                    } else {
                        return '--';
                    }
                },
            },

            {
                field: 'pending',
                headerName: 'Pending',
                headerComponentFramework: (params: IHeaderParams) => (
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>
                            Pending
                            {([WageAndTravelCategory.PendingPayment, WageAndTravelCategory.PendingWire, WageAndTravelCategory.PaymentError].indexOf(props.selectedTab) >= 0 || (context.isNkrSuper && props.selectedTab == WageAndTravelCategory.PendSurgery)) &&
                                <>
                                    <br />
                                    {`Total: $${getHeaderTotals(props.list).transactionTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                                </>
                            }
                        </HeaderWrapper>
                    </SortableHeaderComponent>
                ),
                width: 155,
                minWidth: 145,
                hide: props.selectedTab == WageAndTravelCategory.Search
                    || props.selectedTab == WageAndTravelCategory.PendingLWRVerify
                    || props.selectedTab == WageAndTravelCategory.PendingDERVerify
                    || props.list.some(f => f.pendingTransactionConfirmation),
                sortable: false,
                valueGetter: (params: ValueGetterParams) => {
                    const pendingProps = {
                        data: params.data,
                        isSuperUser: context.isNkrSuper,
                        category: props.selectedTab,
                        onActionRequest: props.onActionRequest
                    } as IPendingRendererProps;
                    const resolved = resolvePendingCell(pendingProps);
                    if (resolved.kind === 'total' || resolved.kind === 'status') {
                        return resolved.display;
                    }
                    return params.data.status;
                },
                cellRendererParams: { isSuperUser: context.isNkrSuper, category: props.selectedTab, onActionRequest: props.onActionRequest },
                cellRendererFramework: PendingRenderer
            },

            {
                field: 'actionId',
                headerName: 'Actions',
                headerComponentFramework: (params: IHeaderParams) =>
                    <div style={{ width: '100%', textAlign: 'center' }}>Actions</div>,
                width: 220,
                minWidth: 168,
                maxWidth: 280,
                pinned: 'right',
                suppressSizeToFit: true,
                cellClass: 'action-menu',
                headerClass: 'action-menu-header',
                sortable: false,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IWatrRequest;
                    const gridContext = (params.context || {}) as {
                        isSearchView?: boolean;
                        selectedTab?: WageAndTravelCategory;
                        area?: DsArea;
                    };
                    const selectedTab = gridContext.selectedTab != null ? gridContext.selectedTab : props.selectedTab;
                    const area = gridContext.area != null ? gridContext.area : props.area;
                    const isSearchView = gridContext.isSearchView != null ? !!gridContext.isSearchView : !!props.isSearchView;
                    const pendingProps = {
                        ...params,
                        data,
                        isSuperUser: context.isNkrSuper,
                        category: selectedTab,
                        filterCat: selectedTab,
                        onActionRequest: props.onActionRequest
                    } as IPendingRendererProps;
                    const resolved = resolvePendingCell(pendingProps);
                    const hidePrimaryAction = selectedTab === WageAndTravelCategory.Search || isSearchView;
                    const pendingAction = !hidePrimaryAction && resolved.kind === 'action'
                        ? renderPendingActionButton(pendingProps, resolved)
                        : null;

                    return (
                        <div className={['watr-action-cell', pendingAction ? '' : 'watr-action-cell--solo'].filter(Boolean).join(' ')}>
                            {pendingAction ? (
                                <div className='watr-action-cell__primary'>
                                    {pendingAction}
                                </div>
                            ) : null}
                            <div className='watr-action-cell__menu'>
                                <ActionsDropdown {...params}
                                    request={data}
                                    isNkrSuper={context.isNkrSuper}
                                    showBsMenu={false}
                                    area={area}
                                    category={selectedTab}
                                    updateSidebarView={props.onActionRequest}
                                    updateModalView={props.onActionRequest}
                                    onMenuToggle={handleMenuToggle}
                                    onItemSelected={handleItemSelected}
                                />
                            </div>
                        </div>
                    )
                }
            }
        ];

        var columnsToHide = [];
        var newColumns = _.cloneDeep(columns);
        const isPendDocsTab = selectedTab === WageAndTravelCategory.PendDocs;

        if (!context.isNkrSuper) {
            columnsToHide.push(newColumns.findIndex(x => x.field == 'centerAlias'));
        }

        columnsToHide.push(newColumns.findIndex(x => x.field == 'achRoutingNumber'));
        columnsToHide.push(newColumns.findIndex(x => x.field == 'achReimbursementType'));
        columnsToHide.push(newColumns.findIndex(x => x.field == 'bankInfo'));
        columnsToHide.push(newColumns.findIndex(x => x.field == 'fraudFlags'));

        if (isAdvancedReimbursement) {
            columnsToHide.push(newColumns.findIndex(x => x.field == 'birthDate'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'createdDate'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'countryName'));
            const nameCol = newColumns.find(x => x.field == 'name');
            if (nameCol) {
                nameCol.width = 200;
                nameCol.minWidth = 120;
                nameCol.flex = 1;
            }
        } else if (!isPendDocsTab) {
            columnsToHide.push(newColumns.findIndex(x => x.field == 'birthDate'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'createdDate'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'countryName'));
        } else {
            const nameCol = newColumns.find(x => x.field == 'name');
            if (nameCol) {
                nameCol.width = 200;
                nameCol.minWidth = 120;
                nameCol.flex = 1;
            }
        }

        const showStatusOnStandardTab =
            isPendDocsTab
            || selectedTab === WageAndTravelCategory.PendingDERVerify
            || selectedTab === WageAndTravelCategory.PendingPayment
            || selectedTab === WageAndTravelCategory.PendingWire
            || selectedTab === WageAndTravelCategory.PendSurgery;

        if (!isAdvancedReimbursement) {
            columnsToHide.push(newColumns.findIndex(x => x.field == 'watrRequestType'));
            if (!isPendDocsTab) {
                columnsToHide.push(newColumns.findIndex(x => x.field == 'donationType'));
            }
            if (!showStatusOnStandardTab) {
                columnsToHide.push(newColumns.findIndex(x => x.field == 'status'));
            } else {
                columnsToHide.push(newColumns.findIndex(x => x.field == 'pending'));
            }
        }

        if (isAdvancedReimbursement) {
            columnsToHide.push(newColumns.findIndex(x => x.field == 'watrRequestType'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'pending'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'donationDate'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'donationConfirmed'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'pendingTransactionConfirmation'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'pendingTransactionAmount'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'pendingTransactionSendOn'));
            columnsToHide.push(newColumns.findIndex(x => x.field == 'confirmationButton'));
        } else switch (selectedTab) {
            case WageAndTravelCategory.PendDocs:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'donationConfirmed'));
                break;

            case WageAndTravelCategory.PendWageReimburse:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                break;

            case WageAndTravelCategory.PendTravelReceipts:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'pending'));
                break;

            case WageAndTravelCategory.PendTravelReimbursement:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                break;

            case WageAndTravelCategory.Reimbursed:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'birthDate'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'pending'));
                break;

            case WageAndTravelCategory.NotReimbursed:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'birthDate'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'pending'));
                break;

            case WageAndTravelCategory.Search:
                break;

            case WageAndTravelCategory.YearEndReport:
                break;

            case WageAndTravelCategory.Transactions:
                break;

            case WageAndTravelCategory.PendingPayment:               
                columnsToHide.push(newColumns.findIndex(x => x.field == 'donationDate'));
                columnsToHide.push(newColumns.findIndex(x => x.field == 'donationConfirmed'));
                columnsToHide.splice(columnsToHide.findIndex(x => x == newColumns.findIndex(x => x.field == 'achReimbursementType')), 1);
                columnsToHide.splice(columnsToHide.findIndex(x => x == newColumns.findIndex(x => x.field == 'bankInfo')), 1);
                columnsToHide.splice(columnsToHide.findIndex(x => x == newColumns.findIndex(x => x.field == 'fraudFlags')), 1);
                break;

            case WageAndTravelCategory.PaymentError:
                columnsToHide.splice(columnsToHide.findIndex(x => x == newColumns.findIndex(x => x.field == 'fraudFlags')), 1);
                break;
                
            case WageAndTravelCategory.PendSurgery:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'donationConfirmed'));
                if (!context.isNkrSuper) {
                    columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeeklyAmount'));
                    columnsToHide.push(newColumns.findIndex(x => x.field == 'lwrWeekCount'));
                    columnsToHide.push(newColumns.findIndex(x => x.field == 'lwr'));
                    columnsToHide.push(newColumns.findIndex(x => x.field == 'tlr'));
                }
                break;

            case WageAndTravelCategory.PendingLWRVerify:
                columnsToHide.push(newColumns.findIndex(x => x.field == 'isInternationallyEligible'));
                break;

        }

        if (selectedTab !== WageAndTravelCategory.PaymentError) {
            const pendingDonorReviewColIndex = newColumns.findIndex(x => x.field == 'bankingFixesPending');
            if (pendingDonorReviewColIndex >= 0) {
                columnsToHide.push(pendingDonorReviewColIndex);
            }
        }

        columnsToHide.sort((a, b) => a > b ? -1 : 1);

        columnsToHide.forEach((value, index) => {
            newColumns.splice(value, 1);
        });

        return newColumns;
    };

    const fittingRef = useRef(false);
    const lastFitWidthRef = useRef(0);

    const fitGridColumns = (api?: GridApi | null, clientWidth?: number) => {
        if (!api || fittingRef.current) {
            return;
        }

        requestAnimationFrame(() => {
            const root = document.querySelector('#grid-wrapper .ag-root') as HTMLElement | null;
            const measuredWidth = Math.floor(clientWidth || root?.clientWidth || 0);

            if (measuredWidth <= 0) {
                return;
            }

            if (Math.abs(measuredWidth - lastFitWidthRef.current) < 2) {
                return;
            }

            fittingRef.current = true;
            lastFitWidthRef.current = measuredWidth;

            try {
                api.sizeColumnsToFit();
            } finally {
                requestAnimationFrame(() => {
                    fittingRef.current = false;
                });
            }
        });
    };

    const onGridReady = (e: GridReadyEvent) => {
        props.onSetGridComponents(e);
        fitGridColumns(e.api);
    };

    const onDataFirstRendered = (e: FirstDataRenderedEvent) => {
        fitGridColumns(e.api);
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // Only refit on real width changes (window/sidebar), not scrollbar jitter.
        fitGridColumns(e.api, e.clientWidth);
    };

    const onCellClicked = (e: CellClickedEvent) => {
        let data = e.data as IWatrRequest;

        let excludedFields = ['coordinatorId', 'actionId', 'pending', 'name', 'bankInfo', 'fraudFlags', 'confirmationButton'];

        if (excludedFields.indexOf(e.colDef.field) < 0) {
            props.onActionRequest(data.id, WageAndTravelViewType.RequestData);
        }
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridColumnsChanged = (e: GridColumnsChangedEvent) => {
        lastFitWidthRef.current = 0;
        fitGridColumns(e.api);
    };

    const columnDefs = useMemo(() => getColumnChanges(props.selectedTab), [props.selectedTab, props.isSearchView, context.donationTypes, props.list]);

    const rowData = useMemo(() => props.list, [props.list]);

    const gridContext = useMemo(() => ({
        isSearchView: !!props.isSearchView,
        selectedTab: props.selectedTab,
        area: props.area,
    }), [props.isSearchView, props.selectedTab, props.area]);

    useEffect(() => {
        if (!props.gridApi) {
            return;
        }

        props.gridApi.hideOverlay();
    }, [props.isLoading, props.gridApi, props.list]);

    useEffect(() => {
        if (!props.gridApi) {
            return;
        }

        props.gridApi.refreshCells({ columns: ['actionId'], force: true });
    }, [props.isSearchView, props.selectedTab, props.area, props.gridApi, props.list]);

    const isLoading = !!props.isLoading;
    const showEmpty = !isLoading && (!props.list || props.list.length === 0);

    return (
        <div style={{ position: 'relative', width: '100%', height: props.height || '68vh' }}>
            <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' height={props.height}>
                <AgGridReact
                    defaultColDef={{
                        sortable: true,
                        resizable: true,
                        suppressSizeToFit: false
                    }}
                    columnDefs={columnDefs}
                    rowData={isLoading ? [] : rowData}
                    context={gridContext}
                    headerHeight={52}
                    rowHeight={48}
                    enableCellChangeFlash={false}
                    enableCellTextSelection={true}
                    suppressCellSelection={true}
                    suppressNoRowsOverlay={true}
                    suppressHorizontalScroll={true}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={onDataFirstRendered}
                    onSortChanged={onSortChanged}
                    onCellClicked={onCellClicked}
                    onBodyScroll={onBodyScroll}
                    onGridColumnsChanged={onGridColumnsChanged}
                >
                </AgGridReact>
            </GridWrapper>
            {isLoading ? (
                <div
                    role='status'
                    aria-live='polite'
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f5f5f5',
                        zIndex: 5,
                        pointerEvents: 'none',
                    }}
                >
                    <ReactLoading type='spin' color='black' height={64} width={64} />
                    <div
                        style={{
                            color: 'black',
                            fontSize: 18,
                            fontWeight: 600,
                            padding: 5,
                            marginTop: 8,
                            textAlign: 'center',
                        }}
                    >
                        Loading your data... Please wait while requests load for this tab.
                    </div>
                </div>
            ) : showEmpty ? (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        fontSize: 14,
                        pointerEvents: 'none',
                        zIndex: 4,
                    }}
                >
                    No wage &amp; travel requests found...
                </div>
            ) : null}
        </div>
    );
}

export default WatrAgGrid;
